import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PdfDocType } from '@patient-ui/shared/enums';
import { EnvironmentService } from '@patient-ui/shared-ui/utils';

/** Invoice detail information for a payment transaction. */
export interface InvoiceDetail {
  /** Patient name */
  patientName: string;
  /** Specimen number */
  specimenNumber: string;
  /** Invoice number */
  invoiceNumber: string;
  /** Date of service (MM/DD/YYYY format) */
  dos: string;
  /** Invoice amount (changed from string to number) */
  invcAmt: number;
  /** Amount paid (changed from string to number) */
  amtPaid: number;
  /** Patient date of birth (yyyy-MM-dd format) */
  patientDob: string;
  /** Responsible party ZIP code */
  respPartyZip: string;
  /** Responsible party first name */
  respPartyFirstName: string;
  /** Responsible party last name */
  respPartyLastName: string;
}

/** Response for the NACHA / card-capture payment authorization PDF endpoints. */
export interface PaymentAuthorizationPdfResponse {
  /** Base64-encoded PDF, empty when verification fails. */
  pdf: string;
  /** List of invoice details - can contain multiple receipts */
  invoiceDetails: InvoiceDetail[];
  /** Additional messages from EPS service. */
  messages?: string[];
  /** Indicates if the EPS request was successful. Backend sends as "successful" in JSON. */
  successful: boolean;
  reasonCode: string;
  reasonDescription: string;
}

@Injectable({
  providedIn: 'root',
})
export class PdfViewerService {
  constructor(
    private readonly http: HttpClient,
    private envService: EnvironmentService
  ) {}

  getPdfDetails(id: string, docType: string): Observable<Blob> {
    let pdfServiceUrl = '';

    if (PdfDocType.Invoice === docType) {
      pdfServiceUrl = `${this.envService.baseUrl}/guest/invoices/paymentPdf?transactionId=${id}`;
    } else if (PdfDocType.Result === docType) {
      pdfServiceUrl = `${this.envService.baseUrl}/guest/${docType}?resultId=${id}`;
    }

    return this.http
      .get(pdfServiceUrl, { headers: this.getHeaders(), responseType: 'blob' })
      .pipe(map((result) => result));
  }

  getInvoicePdfDetails(
    id: string,
    docType: string,
    dob: string,
    portalUser: boolean
  ): Observable<Blob> {
    let pdfServiceUrl = '';
    const uuid =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    if (PdfDocType.Invoice === docType) {
      const portalUserSnippet = portalUser
        ? `/protected/patients/current`
        : `/guest`;
      pdfServiceUrl = `${this.envService.baseUrl}${portalUserSnippet}/invoices/paymentPdf`;
    }
    const request = {
      transactionId: id,
      dateOfBirth: dob,
      uuid: dob.length === 0 ? uuid : '',
    };
    return this.http
      .post(pdfServiceUrl, request, {
        headers: this.getHeaders(),
        responseType: 'blob',
      })
      .pipe(map((result) => result));
  }

  getCombinedInvoicePdfDetails(
    transIdList: string[],
    docType: string,
    portalUser: boolean,
    encryptedIds: boolean,
    dob: string
  ): Observable<Blob> {
    let pdfServiceUrl = '';
    let uuid = '';
    if (dob === '') {
      uuid =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
    }
    if (PdfDocType.Invoice === docType) {
      const portalUserSnippet = portalUser
        ? `/protected/patients/current`
        : `/guest`;
      if (encryptedIds === true) {
        pdfServiceUrl = `${this.envService.baseUrl}${portalUserSnippet}/invoices/paymentCombinedPdf/v2`;
      } else {
        pdfServiceUrl = `${this.envService.baseUrl}${portalUserSnippet}/invoices/paymentCombinedPdf`;
      }
    }
    const request = {
      transIdList: transIdList,
      uuid: uuid ? uuid : '',
      patientDob: dob ? dob : '',
    };
    return this.http
      .post(pdfServiceUrl, request, {
        headers: this.getHeaders(),
        responseType: 'blob',
      })
      .pipe(map((result) => result));
  }

  /**
   * Get payment authorization PDF (NACHA or Card Capture).
   * Frontend sends encrypted transaction ID + DOB + ZIP to backend.
   * Backend decrypts the transaction ID, determines authorization type (nacha/capture),
   * validates credentials and expiration, then returns the appropriate PDF.
   * Via portal-api, e.g. DEV:
   *   POST {api.baseUrl}/guest/invoices/payment-authorization/pdf
   * Backend returns JSON with base64-encoded PDF.
   */
  getPaymentAuthorizationPdf(
    transactionId: string,
    dob: string,
    zipCode: string
  ): Observable<PaymentAuthorizationPdfResponse> {
    const pdfServiceUrl = `${this.envService.baseUrl}/guest/invoices/payment-authorization/pdf`;

    const request = {
      transactionId: transactionId,
      dateOfBirth: dob,
      zipCode: zipCode,
    };

    return this.http.post<PaymentAuthorizationPdfResponse>(
      pdfServiceUrl,
      request
    );
  }

  private getHeaders() {
    return new HttpHeaders({
      Accept: 'application/pdf',
    });
  }
}
